import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ButtonTextDirective, SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId, TextQaId } from '@app/helpers';
import { AddressBookService } from '@app/services';
import {
  NgbAccordion,
  NgbPanel,
  NgbPanelContent,
  NgbPanelHeader,
  NgbPanelToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { BaseSiteToBrandService } from 'haba-base-components';
import {
  ActiveCartService,
  Address,
  GlobalMessageService,
  I18nModule,
  UserAddressService,
} from 'haba-core';
import { combineLatest, of, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Title } from 'src/app/@enums/title';
import { CustomerType } from '../../@enums/customerType';

@Component({
  selector: 'app-address-book',
  templateUrl: './address-book.component.html',
  styleUrls: ['./address-book.component.scss'],
  imports: [
    NgIf,
    NgbAccordion,
    NgbPanel,
    NgbPanelHeader,
    NgbPanelToggle,
    SvgComponent,
    NgbPanelContent,
    NgTemplateOutlet,
    NgFor,
    NgClass,
    ButtonTextDirective,
    I18nModule,
  ],
})
export class AddressBookComponent implements OnInit, OnDestroy {
  titleCode = Title;
  addresses: Address[];
  defaultAddress: Address;
  address: any;
  hasAddress = false;

  baseQaId: HabaQaId;
  qaToggle: ButtonQaId;
  qaAdd: ButtonQaId;
  qaApply: ButtonQaId;
  qaDelete: ButtonQaId;

  additionalInfoTextQaId: TextQaId;
  companyNameTextQaId: TextQaId;
  currentCountryTextQaId: TextQaId;
  nameTextQaId: TextQaId;
  phoneTextQaId: TextQaId;
  postalCodeAndCityTextQaId: TextQaId;
  streetNameAndNumberTextQaId: TextQaId;
  defaultShippingTextQaId: TextQaId;
  defaultBillingTextQaId: TextQaId;

  protected readonly CustomerType = CustomerType;

  private addressSubscription: Subscription;

  constructor(
    private addressBookService: AddressBookService,
    protected globalMessageService: GlobalMessageService,
    private ref: ChangeDetectorRef,
    private baseSiteToBrandService: BaseSiteToBrandService,
    private userAddressService: UserAddressService,
    private activeCartService: ActiveCartService
  ) {}
  ngOnInit(): void {
    this.initIds();
    this.getAddresses();
  }

  ngOnDestroy(): void {
    if (this.addressSubscription) {
      this.addressSubscription.unsubscribe();
    }
  }

  addAddress(): void {}

  takeAddress(address: Address): void {
    this.ref.detectChanges();
    this.addressBookService.setAddress(address);
  }

  isReadOnly(defaultAddress: boolean) {
    return !defaultAddress;
  }

  getAddresses() {
    if (!this.activeCartService.isGuestCart()) {
      this.userAddressService.loadAddresses();
      this.userAddressService.loadDeliveryCountries();

      this.addressSubscription = combineLatest([
        this.userAddressService.getAddressesLoading(),
        this.userAddressService.getDeliveryCountries(),
      ])
        .pipe(
          filter(([loading]) => !loading),
          switchMap(([_, countries]) =>
            combineLatest([of(countries), this.userAddressService.getAddresses()])
          ),
          filter(([countries, addresses]) => !!countries?.length && !!addresses.length),
          map(([countries, addresses]) => {
            const allowedCountries = countries.map((country) => country.isocode);
            return addresses.filter(
              (address) =>
                address.shippingAddress &&
                !address.habaPayerAddress &&
                allowedCountries.includes(address.country?.isocode)
            );
          })
        )
        .subscribe((addresses) => {
          this.addresses = addresses;
          this.hasAddress = addresses.length > 0;
          this.ref.detectChanges();
        });
    }
  }

  private initIds() {
    this.baseQaId = HabaQaId.forPage(HabaQaId.page.deliveryAddress);
    const addressBookId = this.baseQaId.forSection(HabaQaId.section.addressBook);
    this.qaToggle = addressBookId.getButtonId(HabaQaId.elements.button.toggleDropdown);
    this.qaAdd = addressBookId.getButtonId(HabaQaId.elements.button.add);
    this.qaApply = addressBookId.getButtonId(HabaQaId.elements.button.apply);
    this.qaDelete = addressBookId.getButtonId(HabaQaId.elements.button.delete);
    this.additionalInfoTextQaId = addressBookId.getTextId(
      HabaQaId.elements.text.additionalAddressInfo
    );
    this.companyNameTextQaId = addressBookId.getTextId(HabaQaId.elements.text.companyName);
    this.currentCountryTextQaId = addressBookId.getTextId(HabaQaId.elements.text.currentCountry);
    this.nameTextQaId = addressBookId.getTextId(HabaQaId.elements.text.name);
    this.phoneTextQaId = addressBookId.getTextId(HabaQaId.elements.text.phone);
    this.postalCodeAndCityTextQaId = addressBookId.getTextId(
      HabaQaId.elements.text.postalCodeAndCity
    );
    this.streetNameAndNumberTextQaId = addressBookId.getTextId(
      HabaQaId.elements.text.streetNameAndNumber
    );
    this.defaultShippingTextQaId = addressBookId.getTextId(
      HabaQaId.elements.text.defaultShippingAddress
    );
    this.defaultBillingTextQaId = addressBookId.getTextId(
      HabaQaId.elements.text.defaultBillingAddress
    );
  }
}
